import { Stack, Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { ReactNode } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { TextInput } from '@/components/form/baseInputs/TextInput/TextInput';
import { COLORS } from '@/styles/tokens/colors';

import {
  ChatComposer,
  ChatComposerInputProps,
  ChatComposerProps,
  ChatComposerToolbarProps,
} from './ChatComposer';

export interface LargeChatComposerProps
  extends Omit<ChatComposerProps, 'input' | 'toolbar'> {
  /** Decorative element shown at the start of the text entry area. */
  inputStartAdornment?: ReactNode;
  /** Theme spacing between the trailing action and submit button. */
  trailingActionGap?: number;
}

interface LargeChatComposerInputProps extends ChatComposerInputProps {
  inputStartAdornment?: ReactNode;
}

function LargeChatComposerInput({
  inputStartAdornment,
  startAdornment,
  sx,
  ...inputProps
}: LargeChatComposerInputProps) {
  const hasDecorativeAdornment =
    !startAdornment && Boolean(inputStartAdornment);
  const decorativeAdornment = hasDecorativeAdornment ? (
    <Stack
      sx={{
        position: 'absolute',
        top: '16px',
        left: '16px',
        zIndex: 1,
      }}
    >
      {inputStartAdornment}
    </Stack>
  ) : undefined;

  return (
    <TextInput
      {...inputProps}
      startAdornment={startAdornment ?? decorativeAdornment}
      sx={{
        ...(sx as SystemStyleObject<Theme>),
        '& .MuiInputBase-input': {
          boxSizing: 'border-box',
          color: COLORS.TEXT.PRIMARY,
          padding: '16px',
          paddingLeft: hasDecorativeAdornment ? '40px' : '16px',
        },
        '& .MuiOutlinedInput-root': {
          position: 'relative',
          minHeight: 160,
          borderRadius: '16px',
          boxShadow: (theme) => theme.palette.shadows.xsAmbient,
          '&:hover, &.Mui-focused': {
            boxShadow: (theme) => theme.palette.shadows.smAmbient,
          },
        },
      }}
    />
  );
}

function LargeChatComposerToolbar({
  buttonEndIcon,
  buttonLabel,
  buttonVariant,
  leadingAction,
  onSubmit,
  submitDisabled,
  testId,
  trailingAction,
  trailingActionGap = 1.5,
}: ChatComposerToolbarProps & { trailingActionGap?: number }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ width: '100%', mt: 'auto', px: 2, pb: 2 }}
    >
      {leadingAction ?? <span />}
      <Stack direction="row" alignItems="center" gap={trailingActionGap}>
        {trailingAction}
        <Button
          data-testid={testId ? `${testId}-submit` : undefined}
          variant={buttonVariant}
          size="xs"
          sx={{
            height: 'auto',
            minHeight: 24,
            px: '8px',
          }}
          onClick={onSubmit}
          disabled={submitDisabled}
          endIcon={buttonEndIcon}
          iconProps={{ 'aria-hidden': true, size: 16, sx: { ml: '4px' } }}
        >
          {buttonLabel}
        </Button>
      </Stack>
    </Stack>
  );
}

export function LargeChatComposer({
  inputStartAdornment,
  trailingActionGap,
  ...composerProps
}: LargeChatComposerProps) {
  return (
    <ChatComposer
      {...composerProps}
      input={(inputProps) => (
        <LargeChatComposerInput
          {...inputProps}
          inputStartAdornment={inputStartAdornment}
        />
      )}
      toolbar={(toolbarProps) => (
        <LargeChatComposerToolbar
          {...toolbarProps}
          trailingActionGap={trailingActionGap}
        />
      )}
    />
  );
}
